/**
 * Uplifted WHOLE from apps/web `utils/notificationAudio` (47 lines).
 *
 * WEB-ONLY, verified: `apps/mobile/src/utils` has no `notificationAudio*` at all (checked with a
 * glob, not assumed — hazard 32). The module is moved entire rather than by symbol because its
 * three module-scope variables ARE its state: lifting `stopSound` alone would leave the timers it
 * clears living in a different module from the ones `playSound` creates.
 *
 * ⚠️ `Audio` / `HTMLAudioElement` are declared locally. `packages/core`'s tsconfig has no `DOM`
 * lib (the same reason `helpers/saleHelpers` declares `structuredClone`), and adding one would
 * make every browser global visible to files that must never touch one. The declarations are
 * types only — erased at emit — and this file is bundled on web alone.
 *
 * 🔴 **`HTMLAudioElement` is an `interface`, NOT a `declare class`, and that is load-bearing.**
 * `tsc` accepts either; CRA's babel pipeline rejects the class form outright — "TypeScript
 * 'declare' fields must first be transformed by @babel/plugin-transform-typescript", a plugin
 * ORDERING error about class fields that no typecheck can see. Caught by the production build,
 * not by `tsc`. Keep type-only shims to `interface` / `declare const` / `declare function`.
 */
interface HTMLAudioElement {
  currentTime: number;
  play(): Promise<void>;
  pause(): void;
}
declare const Audio: { new (src?: string): HTMLAudioElement };

const SOUNDS = {
  notification:  '/notification.mp3',
  notification1: '/Notification1.mp3',
} as const;

const LOOP_INTERVAL_MS = 9_000;
const AUTO_STOP_MS     = 240_000;

let audio:       HTMLAudioElement | null = null;
let loopId:      ReturnType<typeof setInterval> | null = null;
let stopTimerId: ReturnType<typeof setTimeout>  | null = null;

export function playSound(sound: keyof typeof SOUNDS, loop = false): void {
  stopSound();

  audio = new Audio(SOUNDS[sound]);
  audio.play().catch(() => { /* gesture-blocked or 404 — silent */ });

  if (!loop) return;

  loopId = setInterval(() => {
    if (!audio) return;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }, LOOP_INTERVAL_MS);

  stopTimerId = setTimeout(stopSound, AUTO_STOP_MS);
}

export function stopSound(): void {
  if (loopId      !== null) { clearInterval(loopId);     loopId      = null; }
  if (stopTimerId !== null) { clearTimeout(stopTimerId); stopTimerId = null; }
  if (audio) { audio.pause(); audio = null; }
}
